import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { UIMessage } from 'ai';
import { usePermissions } from 'access/usePermission';
import useUserStore from 'store/useUserStore';
import { openErrorToaster } from 'services/toast.service';
import { BagelChatMessage, BagelConversationSummary } from '../../types';
import {
  BAGEL_WORKFLOW_ID_STORAGE_KEY,
  getWorkflowSession,
  listWorkflowSessions,
  saveWorkflowSession,
} from '../../api/bagel-workflow-session';
import { downloadBagelChatConversation } from '../lib/downloadBagelChatConversation';
import { getAssistantDisplayText } from '../lib/assistantMessageContent';
import { toApiMessageContent, parsePersistedMessageParts } from '../lib/toBagelApiMessages';
import useBagelChatStream from '../hooks/useBagelChatStream';

type MessageExtras = {
  thinkingTool?: string;
  thinkingText?: string;
  timestamp?: string;
};

type StreamDataEvent = {
  type?: string;
  tool?: string;
  text?: string;
};

function isChatStreaming(status: string): boolean {
  return status === 'submitted' || status === 'streaming';
}

export interface BagelChatAttachment {
  url: string;
  filename?: string;
  mediaType?: string;
}

interface BagelChatContextValue {
  isOpen: boolean;
  messages: BagelChatMessage[];
  isStreaming: boolean;
  streamingMessageId: string | null;
  pageContext: string | undefined;
  activeWorkflowId: string | null;
  conversations: BagelConversationSummary[];
  isLoadingConversations: boolean;
  showHistory: boolean;
  setPageContext: (ctx: string | undefined) => void;
  setShowHistory: (open: boolean) => void;
  toggleOpen: () => void;
  close: () => void;
  send: (content: string, attachment?: BagelChatAttachment) => Promise<void>;
  startNewConversation: () => void;
  loadConversation: (workflowId: string) => Promise<void>;
  refreshConversations: () => Promise<void>;
  downloadConversation: () => void;
}

const BagelChatContext = createContext<BagelChatContextValue | null>(null);

function getMessageText(message: UIMessage, isStreaming = false): string {
  return getAssistantDisplayText(message, { isStreaming });
}

function toUiMessages(
  restored: Pick<BagelChatMessage, 'role' | 'content'>[],
): { messages: UIMessage[]; extras: Record<string, MessageExtras> } {
  const messages = restored.map((m, index) => ({
    id: `restored-${index}-${m.role}`,
    role: m.role,
    // Reverses an uploaded image's URL annotation back into a real file part — without this, a
    // restored session only ever had `{type: 'text', text: m.content}`, which silently dropped any
    // attachment: no thumbnail, and (since the annotation lived only in the parts the model never
    // sees again) no way for Bagel to still reference the upload after a refresh.
    parts: parsePersistedMessageParts(m.content),
  }));
  return { messages, extras: {} };
}

function toPersistableMessages(
  uiMessages: UIMessage[],
): Pick<BagelChatMessage, 'role' | 'content'>[] {
  return uiMessages.map(m => ({
    role: m.role as 'user' | 'assistant',
    // toApiMessageContent, not getMessageText — this is a second, independent flattening path
    // that used to skip file-part handling entirely: an uploaded image's URL survived the live
    // turn but was silently dropped the moment the session was persisted, so it vanished from the
    // model's context after any refresh even though the outgoing request for that same turn had
    // it right. Same function as the live API request now, so it can't drift out of sync again.
    content: toApiMessageContent(m),
  }));
}

function persistWorkflowId(workflowId: string | null) {
  if (typeof sessionStorage === 'undefined') return;
  if (workflowId) {
    sessionStorage.setItem(BAGEL_WORKFLOW_ID_STORAGE_KEY, workflowId);
  } else {
    sessionStorage.removeItem(BAGEL_WORKFLOW_ID_STORAGE_KEY);
  }
}

export function BagelChatProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [pageContext, setPageContext] = useState<string | undefined>(
    undefined,
  );
  const [activeWorkflowId, setActiveWorkflowId] = useState<string | null>(
    typeof sessionStorage !== 'undefined'
      ? sessionStorage.getItem(BAGEL_WORKFLOW_ID_STORAGE_KEY)
      : null,
  );
  const [conversations, setConversations] = useState<
    BagelConversationSummary[]
  >([]);
  const [isLoadingConversations, setIsLoadingConversations] =
    useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [messageExtras, setMessageExtras] = useState<
    Record<string, MessageExtras>
  >({});

  const workflowIdRef = useRef<string | null>(activeWorkflowId);
  const processedDataRef = useRef(0);
  const wasLoadingRef = useRef(false);

  const user = useUserStore(s => s.user);
  const { can } = usePermissions();
  const canUseBagelChat = !!user && can('bagel:chat');

  const chatMessagesRef = useRef<UIMessage[]>([]);

  const handleThinkingData = useCallback((dataPart: { type: string; data?: unknown }) => {
    if (dataPart.type !== 'data-thinking') return;
    const item = dataPart.data as StreamDataEvent;
    if (item.type !== 'thinking' || !item.tool || !item.text) return;

    const lastAssistant = [...chatMessagesRef.current]
      .reverse()
      .find(m => m.role === 'assistant');
    const targetId = lastAssistant?.id;
    if (!targetId) return;

    setMessageExtras(prev => ({
      ...prev,
      [targetId]: {
        ...prev[targetId],
        thinkingTool: item.tool,
        thinkingText: item.text,
      },
    }));
  }, []);

  const {
    messages: chatMessages,
    sendMessage,
    setMessages: setChatMessages,
    status,
    stop,
  } = useBagelChatStream(pageContext, { onData: handleThinkingData });

  const isStreaming = isChatStreaming(status);
  chatMessagesRef.current = chatMessages;

  const refreshConversations = useCallback(async () => {
    if (!canUseBagelChat) return;
    setIsLoadingConversations(true);
    try {
      const sessions = await listWorkflowSessions();
      setConversations(sessions);
    } catch {
      // Non-blocking — history panel can retry
    } finally {
      setIsLoadingConversations(false);
    }
  }, [canUseBagelChat]);

  const applySession = useCallback(
    (
      workflowId: string,
      sessionMessages: Pick<BagelChatMessage, 'role' | 'content'>[],
      sessionPageContext?: string | null,
    ) => {
      const { messages, extras } = toUiMessages(sessionMessages);
      workflowIdRef.current = workflowId;
      setActiveWorkflowId(workflowId);
      persistWorkflowId(workflowId);
      setChatMessages(messages);
      setMessageExtras(extras);
      setPageContext(sessionPageContext ?? undefined);
      setShowHistory(false);
      processedDataRef.current = 0;
    },
    [setChatMessages],
  );

  useEffect(() => {
    if (!canUseBagelChat) return;

    refreshConversations();

    const savedId = workflowIdRef.current;
    if (!savedId) return;

    getWorkflowSession(savedId)
      .then(session => {
        if (session?.messages?.length) {
          applySession(savedId, session.messages, session.pageContext);
        }
      })
      .catch(() => {});
  }, [applySession, canUseBagelChat, refreshConversations]);

  useEffect(() => {
    if (wasLoadingRef.current && !isStreaming) {
      const wid = workflowIdRef.current;
      if (wid) {
        saveWorkflowSession(
          wid,
          toPersistableMessages(chatMessages),
          pageContext,
        )
          .then(() => refreshConversations())
          .catch(() => {});
      }
    }
    wasLoadingRef.current = isStreaming;
  }, [chatMessages, isStreaming, pageContext, refreshConversations]);

  const streamingMessageId = useMemo(() => {
    if (!isStreaming) return null;
    const lastAssistant = [...chatMessages]
      .reverse()
      .find(m => m.role === 'assistant');
    return lastAssistant?.id ?? null;
  }, [chatMessages, isStreaming]);

  const messages = useMemo(
    (): BagelChatMessage[] =>
      chatMessages.map((m: UIMessage) => {
        const extra = messageExtras[m.id] || {};

        return {
          role: m.role as 'user' | 'assistant',
          content: getMessageText(m, streamingMessageId === m.id),
          parts: m.parts as BagelChatMessage['parts'],
          clientId: m.id,
          thinkingTool: extra.thinkingTool,
          thinkingText: extra.thinkingText,
          timestamp: extra.timestamp,
        };
      }),
    [chatMessages, messageExtras, streamingMessageId],
  );

  const toggleOpen = useCallback(() => setIsOpen(prev => !prev), []);
  const close = useCallback(() => setIsOpen(false), []);

  const resetStreamingState = useCallback(() => {
    stop();
    processedDataRef.current = 0;
  }, [stop]);

  const startNewConversation = useCallback(() => {
    resetStreamingState();
    setChatMessages([]);
    setMessageExtras({});
    workflowIdRef.current = null;
    setActiveWorkflowId(null);
    persistWorkflowId(null);
    setShowHistory(false);
  }, [resetStreamingState, setChatMessages]);

  const loadConversation = useCallback(
    async (workflowId: string) => {
      if (isStreaming) return;

      resetStreamingState();
      try {
        const session = await getWorkflowSession(workflowId);
        if (!session?.messages?.length) {
          await refreshConversations();
          return;
        }
        applySession(workflowId, session.messages, session.pageContext);
      } catch {
        // Session may have expired
      }
    },
    [applySession, isStreaming, refreshConversations, resetStreamingState],
  );

  const downloadConversation = useCallback(() => {
    downloadBagelChatConversation(messages, pageContext);
  }, [messages, pageContext]);

  const send = useCallback(
    async (content: string, attachment?: BagelChatAttachment) => {
      if ((!content.trim() && !attachment) || isStreaming) return;

      if (!workflowIdRef.current) {
        const newId = crypto.randomUUID();
        workflowIdRef.current = newId;
        setActiveWorkflowId(newId);
        persistWorkflowId(newId);
      }

      const userId = crypto.randomUUID();
      setMessageExtras(prev => ({
        ...prev,
        [userId]: { timestamp: new Date().toISOString() },
      }));
      setShowHistory(false);

      const parts: UIMessage['parts'] = [];
      if (attachment) {
        parts.push({
          type: 'file',
          url: attachment.url,
          filename: attachment.filename,
          mediaType: attachment.mediaType ?? 'image/*',
        });
      }
      if (content.trim()) {
        parts.push({ type: 'text', text: content.trim() });
      }

      try {
        await sendMessage({ id: userId, role: 'user', parts });
      } catch (err) {
        openErrorToaster(
          err instanceof Error
            ? err.message
            : 'Bagel chat failed. Please try again.',
          5000,
        );
      }
    },
    [sendMessage, isStreaming],
  );

  const value = useMemo(
    () => ({
      isOpen,
      messages,
      isStreaming,
      streamingMessageId,
      pageContext,
      activeWorkflowId,
      conversations,
      isLoadingConversations,
      showHistory,
      setPageContext,
      setShowHistory,
      toggleOpen,
      close,
      send,
      startNewConversation,
      loadConversation,
      refreshConversations,
      downloadConversation,
    }),
    [
      isOpen,
      messages,
      isStreaming,
      streamingMessageId,
      pageContext,
      activeWorkflowId,
      conversations,
      isLoadingConversations,
      showHistory,
      toggleOpen,
      close,
      send,
      startNewConversation,
      loadConversation,
      refreshConversations,
      downloadConversation,
    ],
  );

  return (
    <BagelChatContext.Provider value={value}>
      {children}
    </BagelChatContext.Provider>
  );
}

export function useBagelChat(): BagelChatContextValue {
  const ctx = useContext(BagelChatContext);
  if (!ctx)
    throw new Error('useBagelChat must be used within BagelChatProvider');
  return ctx;
}
